import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { playVictory, playSpinStart, playSpinTick } from "../lib/sound";
import { Trophy, Sparkles } from "lucide-react";

/**
 * Real-time synced Casino Wheel.
 *
 * Props:
 *  - members: [{user_id, name}]
 *  - lastDraw: {draw_id, round, winner_id, winner_name, payout, auto_credit, drawn_at}
 *  - currentUserId: string
 *  - frequencyMs: animation duration (default 5000)
 *
 * Sync model: every member of the pool polls the backend every 1-2s.
 * When a NEW lastDraw arrives (new draw_id), all clients animate to the SAME winner
 * with the same deterministic angle. Result: synced spin across all open browsers.
 */
export default function SyncedCasinoWheel({ members = [], lastDraw, currentUserId, frequencyMs = 5000 }) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [revealedWinner, setRevealedWinner] = useState(null);
  const lastDrawId = useRef(null);
  const tickerRef = useRef(null);

  const segments = members.length || 1;
  const segAngle = 360 / segments;

  useEffect(() => {
    if (!lastDraw?.draw_id) return;
    if (lastDraw.draw_id === lastDrawId.current) return;
    lastDrawId.current = lastDraw.draw_id;

    // Auto-credit (2-person final round) → no spin, just reveal + celebrate
    if (lastDraw.auto_credit) {
      setRevealedWinner(lastDraw);
      celebrate(lastDraw.winner_id === currentUserId);
      return;
    }

    // Find winner index for deterministic landing angle
    const idx = Math.max(0, members.findIndex((m) => m.user_id === lastDraw.winner_id || m.name === lastDraw.winner_name));
    // Land winner exactly under the top pointer.
    // The wheel rotates CCW visually; our pointer is at top (0deg).
    // Target = 360deg * fullSpins - (idx * segAngle + segAngle/2)
    const target = 360 * 6 + (360 - (idx * segAngle + segAngle / 2));
    setSpinning(true);
    setRevealedWinner(null);
    playSpinStart();
    setRotation((r) => r + target);

    // Ticker sound — decelerating click ticks
    let elapsed = 0;
    const baseInterval = 80;
    const tick = () => {
      // Slow down ticks over time
      const factor = 1 + (elapsed / frequencyMs) * 5;
      playSpinTick(1400 - Math.min(1100, elapsed / 4));
      elapsed += baseInterval * factor;
      if (elapsed < frequencyMs - 300) {
        tickerRef.current = setTimeout(tick, baseInterval * factor);
      }
    };
    tickerRef.current = setTimeout(tick, baseInterval);

    const t = setTimeout(() => {
      setSpinning(false);
      setRevealedWinner(lastDraw);
      celebrate(lastDraw.winner_id === currentUserId);
      if (tickerRef.current) clearTimeout(tickerRef.current);
    }, frequencyMs);

    return () => {
      clearTimeout(t);
      if (tickerRef.current) clearTimeout(tickerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastDraw?.draw_id]);

  const celebrate = (isWinner) => {
    playVictory();
    // Full-screen confetti
    const duration = 3500;
    const end = Date.now() + duration;
    const colors = isWinner
      ? ["#D4AF37", "#FFD700", "#00E676", "#00E5FF", "#FF9500"]
      : ["#00E5FF", "#4DECFF", "#00E676"];
    (function frame() {
      confetti({
        particleCount: isWinner ? 8 : 4,
        angle: 60, spread: 65, startVelocity: 45,
        origin: { x: 0, y: 0.7 }, colors,
      });
      confetti({
        particleCount: isWinner ? 8 : 4,
        angle: 120, spread: 65, startVelocity: 45,
        origin: { x: 1, y: 0.7 }, colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    if (isWinner) {
      // Extra burst from center for the actual winner
      setTimeout(() => {
        confetti({
          particleCount: 200, spread: 120, startVelocity: 55,
          origin: { x: 0.5, y: 0.5 }, colors,
          scalar: 1.4,
        });
      }, 400);
    }
  };

  return (
    <div className="relative flex flex-col items-center" data-testid="synced-casino-wheel">
      <div className="relative w-72 h-72 sm:w-96 sm:h-96">
        {/* Outer glow ring while spinning */}
        {spinning && (
          <div className="absolute -inset-4 rounded-full bg-[#D4AF37]/30 blur-2xl animate-pulse pointer-events-none" />
        )}
        <div className="roulette-pointer" />
        <div
          className="roulette-wheel w-full h-full"
          style={{ transform: `rotate(${rotation}deg)`, transitionDuration: `${frequencyMs}ms` }}
          data-testid="wheel-rotor"
        >
          {/* Segment labels */}
          {members.slice(0, segments).map((m, i) => {
            const angle = i * segAngle + segAngle / 2;
            return (
              <div key={m.user_id || i}
                className="absolute left-1/2 top-1/2 origin-left text-[10px] sm:text-xs font-semibold pointer-events-none"
                style={{
                  transform: `rotate(${angle}deg) translate(${segments > 8 ? 70 : 90}px, -50%)`,
                  color: m.won ? "#4A5970" : "#F0F6FF",
                  textShadow: "0 1px 3px rgba(0,0,0,0.8)",
                  width: "80px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                <span>{m.name?.slice(0, 8)}</span>
              </div>
            );
          })}
          <div className="absolute inset-1/4 rounded-full bg-[#050E1A] border-4 border-[#D4AF37] flex items-center justify-center shadow-2xl">
            <div className="text-center">
              {spinning ? (
                <>
                  <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-1 animate-spin" />
                  <div className="text-base text-cyan-gradient font-bold">{spinning ? "..." : "قرعة"}</div>
                </>
              ) : revealedWinner ? (
                <div>
                  <Trophy className="w-7 h-7 text-[#D4AF37] mx-auto mb-1" />
                  <div className="text-[10px] text-[#8FA3BD]">الفائز</div>
                  <div className="text-sm font-bold text-cyan-gradient max-w-[100px] truncate" data-testid="wheel-winner-name">{revealedWinner.winner_name}</div>
                  <div className="text-[10px] text-[#00E676] font-bold">+${revealedWinner.payout?.toFixed(2)}</div>
                </div>
              ) : (
                <div>
                  <div className="text-xl text-cyan-gradient font-bold">قرعة عادلة</div>
                  <div className="text-[10px] text-[#8FA3BD] mt-1">Fair Draw</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
